import axios from 'axios';
import qs from 'qs';
import CONFIG from '@/config';
import { getSStorage, getLStorage, getAppVer, guid } from '@/utils';
import getQueryVal from '@/utils/getQueryVal';
import { sm2Decrypt } from '@/utils/sm2Util';
import Sentry from '@/sentry';
import { ApiError } from '@/sentry/error';
import { sentryReport } from '@/sentry/errorReport';
import { aesDecrypt } from '@/utils/aesUtil';
import { getTdBlackbox } from '@/utils/tdfp';
// import { getSmDeviceId } from '@/utils/smfp';
const getRequestUrl = (config) => {
    if (!config) return 'Unknown Request';
    const baseURL = config.baseURL || '';
    const url = config.url ? config.url.split('?')[0] : '';
    return `${baseURL}${url}`;
};

const getErrorCode = (res) => {
    console.log('res', res);
    return res?.data?.retcode ?? res?.data?.code ?? res?.status ?? 'UNKNOWN_CODE';
};

const getErrorMessage = (res) => {
    return res?.data?.retmsg || res?.data?.msg || res?.statusText || '接口请求异常';
};

const Axios = axios.create({
    timeout: CONFIG.apiTimeout, // 默认超时时间
});

// 请求头拦截器
Axios.interceptors.request.use(
    (config) => config,
    (err) => Promise.reject(err)
);

// 返回体拦截器
Axios.interceptors.response.use(res => {
    // loading结束
    // 如果返回的是下载流
    if (/(octet-stream|x-download)/.test(res.headers['content-type'])) {
        return res.data;
    }

    // 检查是否登录过期
    const isLoginExpired = +res.data?.retcode === 10002 || +res.data?.code === 10002;
    const isSucccessReq = [0, 200].includes(res.data?.retcode) || [0, 200].includes(res.data?.code);
    try {
        if (!isLoginExpired && !isSucccessReq) {
            Sentry.withScope(scope => {
                const errorCode = getErrorCode(res);
                const errorMessage = getErrorMessage(res);
                const requestUrl = getRequestUrl(res?.config);

                scope.addBreadcrumb({
                    category: '请求信息',
                    level: 'info',
                    message: requestUrl,
                    data: res.config,
                });

                scope.addBreadcrumb({
                    category: '错误信息',
                    level: 'error',
                    data: res.data,
                });

                Sentry.captureException(
                    new ApiError(`${errorMessage} | ${requestUrl} | ${errorCode}`)
                );
            });
        }
    } catch (error) {
        console.error('请求错误Sentry上报失败', error);
    }

    // 如果请求相应为200
    if (Number(res.status) === 200) {
        // 检查是否需要解密响应数据
        const encryptionType = res?.headers?.['x-encryption'] || res?.headers?.['X-Encryption'];
        console.log('encryptionType----', encryptionType);
        const needDecrypt = encryptionType === '1' || encryptionType === '2';
        console.log('needDecrypt----', needDecrypt);
        if (needDecrypt && typeof res?.data === 'string') {
            try {
                const decrypt = encryptionType === '1' ? sm2Decrypt : aesDecrypt;
                const decryptedData = decrypt(res.data);
                // 尝试解析为 JSON
                try {
                    res.data = JSON.parse(decryptedData);
                } catch {
                    res.data = decryptedData;
                }
            } catch (decryptError) {
                sentryReport({
                    name: '接口响应解密失败',
                    error: decryptError,
                    context: {
                        encryptionType,
                        hasEncryptedData: !!res.data,
                        url: res.config?.url,
                    },
                });
            // 解密失败，继续使用原始数据
            }
        }
        return res.data;
    }

    // message
    const msg = res.data.retmsg || res.statusText;
    // 错误实例
    const err = new Error(`报错code:${res.data.retcode},报错内容:${msg}`);
    return Promise.reject(err);
}, err => {
    console.log('=====', err);
    try {
        const errorCode = getErrorCode(err);
        const errorMessage = getErrorMessage(err);
        const requestUrl = getRequestUrl(err?.config);

        Sentry.withScope(scope => {
            if (err.config) {
                scope.addBreadcrumb({
                    category: '请求信息',
                    level: 'info',
                    message: requestUrl,
                    data: err.config,
                });
            }

            if (err.response) {
                scope.addBreadcrumb({
                    category: '错误响应',
                    level: 'error',
                    data: err.response,
                });
            }

            // 上报原始错误
            Sentry.captureException(
                new ApiError(`${errorMessage} | ${requestUrl} | ${errorCode}`)
            );
        });
    } catch (error) {
        console.error('请求错误Sentry上报失败', error);
    }
    return Promise.reject(err);
});
const AxiosObj = async(type, url, data = {}, option = {}) => {
    // 携带的默认参数
    const defaultParam = {
        token: getLStorage('token') || getSStorage('token') || getQueryVal('token'), // 用户token, 优先取'localStorage'的token
        platType: getSStorage('platType') || '5',
        appVer: getAppVer() || '4.2.0',
        channelId: 300001,
        activitySource: getSStorage('activitySource'),
        activityId: getSStorage('activityId'),
        activeId: getSStorage('activeId'),
        clientId: getSStorage('clientId') || getQueryVal('clientId') || guid(),
    };

    let insideData = data;

    const fpHeaders = {
        'At-Client-Code': getTdBlackbox(), // 同盾风控
        // 'At-Client-Df-V2': getSmDeviceId(), // 数美风控
    };

    /**
     * url上有参数，会出现双重赋值
     */
    const urlArr = url.split('?');
    url = urlArr[0];
    const urlParams = qs.parse(urlArr[1]);
    // 仅在请求目标为 ssoServer 时携带 At-* 头
    const targetBaseURL = option.baseURL || CONFIG.server;
    const isSSORequest = targetBaseURL === CONFIG.ssoServer || (url.startsWith('http') && url.includes(CONFIG.ssoServer));
    const headersBase = isSSORequest
        ? {
            'At-Platform-Type': defaultParam.platType,
            'At-App-Version': defaultParam.appVer,
            'At-Channel-Id': defaultParam.channelId,
            'At-Client-Id': defaultParam.clientId,
            ...fpHeaders,
        }
        : fpHeaders;

    // 默认axios对象
    const obj = {
        url, // 请求地址
        baseURL: CONFIG.server,
        dataType: 'json', // 默认格式json
        withCredentials: true, // 是否携带跨域凭证
        method: type.toString().toLocaleLowerCase(),
        params: {}, // url携带参数
        headers: headersBase,
        ...option, // 合并设置参数
    };
    switch (type.toString().toLocaleLowerCase()) {
        case 'get':
            // 合并请求参数
            obj.params = {
                r: Math.random(), // 防止缓存的随机数
                ...defaultParam,
                ...urlParams,
                ...insideData,
            };
            break;
        case 'post':
            // 合并url带的参数
            obj.params = {
                ...defaultParam,
                ...urlParams,
                ...(insideData.queryObj || {}),
            };
            // 删除url上需要带的参数
            delete insideData.queryObj;
            // 合并请求体参数
            // Object.assign(insideData, defaultParam)
            // 默认post提交为json，如果dataType设置为formData则表示使用form提交
            if (obj.dataType === 'formData') {
                // 修改头部contentType
                obj.headers = {
                    ...obj.headers,
                    'Content-Type': 'application/x-www-form-urlencoded;',
                };
                // 格式化form表单参数
                insideData = qs.stringify(insideData);
            } else {
                obj.headers = {
                    ...obj.headers,
                    'Content-Type': 'application/json',
                };
            }
            obj.data = insideData;
            break;
        default:
            break;
    }

    obj.paramsSerializer = function query(params) {
        return qs.stringify(params);
    };
    return Axios(obj);
};

export default AxiosObj;
